import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {JWTUser} from "@models/jwt-data.model";
import {AuthenticationInfoModalComponent} from "@app/authentication/components";
import {AuthenticationService} from "@app/authentication/services/authentication.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Log} from "ng2-logger";
import {TranslateService} from "@ngx-translate/core";
import {PasswordConfirmModalComponent} from "@app/authentication/components/password-confirm-modal/password-confirm-modal.component";
import {NotificationService} from '@services/notification.service';
import {Subscription} from "rxjs";
import {ToastsManager} from "@app/shared/util/toastr.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {ResolutionService} from "@services/resolution.service";
import { UserBusinessType } from '@app/shared/models/enums/user-business-type.enum';

@UntilDestroy()
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
    _logger = Log.create(LoginComponent.name);
    @ViewChild(AuthenticationInfoModalComponent, {static: true}) authInfoModal: AuthenticationInfoModalComponent;
    @ViewChild(PasswordConfirmModalComponent, {static: true}) passwordConfirmModal: PasswordConfirmModalComponent;

    focused = true;
    loginUsingCredentials = false;
    forgotPassword = false;
    loginError = false;
    isLoginInProgress = false;
    languageKey = "user-language";
    userHasBoschId = false;
    user: JWTUser;
    login: boolean;
    showValidations = false;
    userIdentity: string;
    isMobile = false;
    signalRConnectedSubscription: Subscription;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthenticationService,
        private notificationService: NotificationService,
        private translationService: TranslateService,
        private resolutionService: ResolutionService,
        private toastr: ToastsManager) {

        // Only clear storage if not coming from legacy auth redirect (already cleared by guard)
        if (this.route.snapshot.queryParams['legacyAuth'] !== 'true') {
            this.authService.clearAuthStorage();
        }

        this.resolutionService.screenSize$.pipe(untilDestroyed(this)).subscribe(screen => {
            this.isMobile = screen.mobile;
            if (this.isMobile) {
                this.loginUsingCredentials = true;
            }
        })
    }

    ngOnInit() {
        const boschId = this.route.snapshot.queryParams['boschId'];
        if (boschId !== undefined) {
            localStorage.setItem('bdmLogin', 'true');
            // Set legacy auth flag to prevent MSAL redirect after successful badge scan login
            this.authService.setLegacyAuthRequired(true);
            this.signalRConnectedSubscription = this.notificationService.signalRConnectedObservable.subscribe(() => {
                this.onScan(boschId)
            });
        }
    }

    onScan(boschId: string) {
        this._logger.info('onScan:: Badge scan input received. Attempting login with boschId:' + boschId);
        this.isLoginInProgress = true;
        this.authService.loginWithBoschId(boschId).subscribe(
            (data: any) => {
                this.handleLogin(data);
                this.userHasBoschId = true;
                this.isLoginInProgress = false;
            },
            (error: any) => {
                this.handleError(error);
                this.isLoginInProgress = false;
            }
        );
    }

    insertBoschId(boschId) {
        this.authService.addUserBoschId(boschId).subscribe(
            (data: any) => {
                this.handleLogin(data);
                this.isLoginInProgress = false;
                this.authInfoModal.isScanninInProgress = false;
            },
            (error: any) => {
                this.handleError(error);
                this.isLoginInProgress = false;
                this.authInfoModal.isScanninInProgress = false;
            })
    }

    onConfirmPassword(formData) {
        this.authService.resetPassword(formData).subscribe(
            (data) => {
                this._logger.info('onConfirmChangePassword:: API returned data: ', data);
                this.toastr.info(this.translationService.instant('auth.toasts.passwordChangeSuccess'));
                this.passwordConfirmModal.close();
            }, (error) => {
                this._logger.error('onConfirmChangePassword:: API returned error: ', error);
                this.passwordConfirmModal.close();
            }
        );
    }

    onCredentialsSwitch() {
        this.loginUsingCredentials = !this.loginUsingCredentials;
        this.forgotPassword = false;
    }

    translate(language) {
        this.translationService.use(language);
        localStorage.setItem(this.languageKey, language);
    }

    private handleLogin(data) {
        this._logger.info('handleLogin:: API returned data: ', data);

        if (data.accountStatus === "PASSWORD_SHOULD_BE_CHANGED") {
            this.userIdentity = data.identity;
            this.changeDefaultPassword();
            return;
        }

        this.user = this.authService.getUserDetails();
        const returnUrl = this.route.snapshot.queryParams['returnUrl'];

        if (this.isTechnicalUser(this.user)) {
            this.loginError = false;
            this.authInfoModal.close();
            this.navigateAfterLogin(returnUrl);
            return;
        }

        if (!this.user.boschId) {
            this.userHasBoschId = false;
            this.authInfoModal.open();
            return;
        }

        this.userHasBoschId = true;
        this.authInfoModal.stopScanningProcess();

        if (!this.user.role) {
            this.authInfoModal.open();
            return;
        }

        this.loginError = false;
        this.authInfoModal.close();

        this.navigateAfterLogin(returnUrl);
    }

    private isTechnicalUser(user: JWTUser): boolean {
        return user?.userBusinessType === UserBusinessType.TechnicalProductionUser ||
            user?.userBusinessType === UserBusinessType.TechnicalToolManagementUser;
    }

    private changeDefaultPassword() {
        this.passwordConfirmModal.open();
    }

    private navigateAfterLogin(returnUrl?: string): void {
        switch (this.user?.userBusinessType) {
            case UserBusinessType.TechnicalToolManagementUser:
                this.router.navigate(['/dashboard']);
                return;
            case UserBusinessType.TechnicalProductionUser:
                this.router.navigate(['/scan-badge']);
                return;
            case UserBusinessType.EmployeeUser:
            default:
                this.router.navigate([returnUrl || '/']);
                return;
        }
    }

    private handleError(error) {
        this._logger.error('handleError:: API returned error: ', error);

        if (error.status === 401) {
            this.loginError = true;
        }
        if (error.status === 409) {
            this.authInfoModal.stopScanningProcess();
        }
    }

    @HostListener('window:focus', ['$event'])
    onFocus() {
        this.focused = true;
    }

    @HostListener('window:blur', ['$event'])
    onBlur() {
        this.focused = false;
    }

}
